import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrackingLink } from "@/components/TrackingLink";
import { toast } from "sonner";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

const TONE = {
  slate: { box: "bg-white border-slate-200 hover:border-slate-400", label: "text-slate-700" },
  orange: { box: "bg-white border-orange-200 hover:border-orange-400", label: "text-orange-700" },
  emerald: { box: "bg-white border-emerald-200 hover:border-emerald-400", label: "text-emerald-700" },
  sky: { box: "bg-white border-sky-200 hover:border-sky-400", label: "text-sky-700" },
  violet: { box: "bg-white border-violet-200 hover:border-violet-400", label: "text-violet-700" },
  amber: { box: "bg-white border-amber-200 hover:border-amber-400", label: "text-amber-700" },
};

function Tile({ k, label, value, sub, to, active, onClick, tone = "slate" }) {
  const tn = TONE[tone] || TONE.slate;
  const cls = `border rounded-md p-4 text-left transition-colors block w-full ${active ? "border-slate-900 bg-slate-50" : tn.box}`;
  const inner = (
    <>
      <div className={`text-[13px] font-bold uppercase tracking-wider font-mono leading-tight ${tn.label}`} data-testid={`inv-kpi-label-${k}`}>{label}</div>
      <div className="font-display text-[42px] font-bold mt-1 leading-none" data-testid={`inv-kpi-count-${k}`}>{value ?? 0}</div>
      {sub && <div className="text-[11px] font-mono text-slate-500 mt-1">{sub}</div>}
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={`inv-kpi-${k}`}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={cls} data-testid={`inv-kpi-${k}`}>{inner}</button>;
}

function Rows({ rows, kind, onAck, acking, t }) {
  if (!rows?.length) return <div className="text-sm text-slate-400 py-3" data-testid={`inv-rows-empty-${kind}`}>{t("Nothing here.")}</div>;
  return (
    <div className="divide-y divide-slate-100" data-testid={`inv-rows-${kind}`}>
      {rows.map((r) => (
        <div key={r.id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`inv-row-${kind}-${r.id}`}>
          <Link to={`/order/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.po_number}</Link>
          {r.tracking && <span className="font-mono text-xs text-slate-600"><TrackingLink number={r.tracking} carrier={r.carrier} testid={`inv-track-${r.id}`} /></span>}
          {r.tracking_status && <Badge className={`border text-[10px] uppercase ${r.tracking_status === "Delivered" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}>{r.tracking_status}</Badge>}
          <span className="text-xs text-slate-500 ml-auto">
            {kind === "arrived" ? `${t("delivered")} ${fmt(r.delivered_at)}` : kind === "received" ? `${t("received")} ${fmt(r.received_at)}` : `${t("shipped")} ${fmt(r.fulfilled_at)}`}
            {r.tracking_last_event?.description ? ` · ${r.tracking_last_event.description.slice(0, 60)}` : ""}
          </span>
          {kind === "arrived" && (
            <Button size="sm" onClick={() => onAck(r.id)} disabled={acking === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2 text-xs" data-testid={`inv-ack-${r.id}`}>
              {acking === r.id ? t("Saving…") : `✓ ${t("Mark received")}`}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// Inventory Packing dashboard counters: Start packing · Ready to ship ·
// Shipped (in transit) · Arrived (tracked delivered, not yet marked received) ·
// Received at warehouse but no label yet. Tracking refreshed every 4 h (17TRACK).
export function InvPackingKpis({ canConfigure }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState("arrived");
  const { data } = useQuery({ queryKey: ["inv-packing-dashboard"], queryFn: async () => (await api.get("/tracking/inv-packing-dashboard")).data, refetchInterval: 60_000 });
  const ack = useMutation({
    mutationFn: (id) => api.post(`/tracking/orders/${id}/delivery-ack`),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["inv-packing-dashboard"] }); toast.success(`${r.data.po_number} · ${t("marked received")}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const poll = useMutation({
    mutationFn: () => api.post("/tracking/poll-now"),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["inv-packing-dashboard"] });
      const s = r.data;
      toast[s.error ? "error" : "success"](s.error ? `${t("Tracking check failed")}: ${s.error}` : `${t("Checked")} ${s.checked} · ${s.delivered} ${t("newly delivered")}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (!data) return null;
  const tr = data.tracking || {};
  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid="inv-packing-kpis">
      <header className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">📦 {t("Inventory Packing")}</h2>
          <p className="text-xs text-slate-500">{t("Tracking numbers are checked every 4 hours")} · {t("last check")} {fmt(tr.last_poll_at)}{tr.last_poll?.error ? ` · ⚠ ${tr.last_poll.error}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {!tr.configured && (
            <Badge className="bg-amber-50 text-amber-900 border border-amber-300" data-testid="inv-tracking-missing">
              {t("17TRACK key missing")}{canConfigure ? <> · <Link to="/settings#tracking" className="underline">{t("Settings")}</Link></> : ` · ${t("ask the Super Admin")}`}
            </Badge>
          )}
          <Button size="sm" variant="outline" onClick={() => poll.mutate()} disabled={poll.isPending || !tr.configured} data-testid="inv-poll-now">{poll.isPending ? t("Checking…") : `🔄 ${t("Check tracking now")}`}</Button>
        </div>
      </header>
      <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile k="start_packing" label={`▶ ${t("Inventory packing")}`} value={data.start_packing} sub={t("waiting to be packed")} to="/orders/inventory_packing" tone="orange" />
        <Tile k="ready_to_ship" label={`✅ ${t("Ready to ship")}`} value={data.ready_to_ship} to="/orders/inventory_packing" tone="emerald" />
        <Tile k="shipped" label={`🚚 ${t("In transit")}`} value={data.shipped} sub={`${t("in transit with tracking")} · ${data.delivered_total} ${t("delivered")}`} active={open === "shipped"} onClick={() => setOpen(open === "shipped" ? null : "shipped")} tone="sky" />
        <Tile k="arrived" label={`📬 ${t("Arrived")}`} value={data.arrived} sub={t("delivered, not marked received")} active={open === "arrived"} onClick={() => setOpen(open === "arrived" ? null : "arrived")} tone="violet" />
        <Tile k="received_no_label" label={`📥 ${t("Received, no label")}`} value={data.received_no_label} sub={t("marked received, label not printed")} active={open === "received"} onClick={() => setOpen(open === "received" ? null : "received")} tone="amber" />
      </div>
      {open && (
        <div className="px-5 pb-4" data-testid={`inv-detail-${open}`}>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-1">
            {open === "arrived" ? t("Arrived — tick once the delivery is confirmed") : open === "received" ? t("Received at the warehouse — waiting for a label") : t("In transit to warehouse")}
          </div>
          <Rows rows={open === "arrived" ? data.arrived_rows : open === "received" ? data.received_rows : data.shipped_rows} kind={open} onAck={(id) => ack.mutate(id)} acking={ack.isPending ? ack.variables : null} t={t} />
        </div>
      )}
    </section>
  );
}
